"use client";

import { EpisodeAccessModel, EpisodeSummaryModel } from "@api/models";
import Link from "next/link";
import "@lib/utils";
import { MdFavoriteBorder } from "react-icons/md";
import { EpisodeIcon } from "./EpisodeIcon";
import { LOG_CATEGORIES, makeClickLogDataAttrs, makeImpLogDataAttrs } from "@lib/analyticLog";
import { ImageWithImpLog } from "./ImageWithImpLog";
import { EpisodeDisplayedOn } from "../../../types/types";
import { AdminEpisodeOptions } from "./AdminEspisodeOptions";
import { MouseEventHandler, useSyncExternalStore } from "react";
import { sessionStore } from "@lib/sessionStore";
import { STORE_KEYS } from "app/constants";
import { twoDigitDate } from "@lib/utils";
import { useStaticStore } from "app/hooks";

export default function EpisodeListItem({
  episode,
  access,
  waitAndFreeWaiting,
  currentEpisodeId,
  displayIndex,
  displayedOn,
}: {
  episode: EpisodeSummaryModel;
  access?: EpisodeAccessModel;
  waitAndFreeWaiting: boolean;
  currentEpisodeId?: string;
  displayedOn: EpisodeDisplayedOn;
  displayIndex: number;
}) {
  const { sessionStatus } = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getServerSnapshot,
  );
  const authorName = episode.author?.displayName || "";
  const publishDate = episode.datePublished ? twoDigitDate(episode.datePublished) : "";
  const images = episode.thumbnailImages || [
    { url: "https://cdn.comici.jp/img/preparing_series.svg" },
  ];
  const { mutate: setRequireSigninPopupOpen } = useStaticStore(STORE_KEYS.requireSigninPopup);
  const handleOnClick: MouseEventHandler = (e) => {
    if (sessionStatus !== "authenticated" && !access?.hasAccess) {
      setRequireSigninPopupOpen(true);
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const imgId = episode.thumbnailImages?.length ? episode.thumbnailImages[0].id : undefined;
  return (
    <div
      className={
        "series-eplist-item" +
        (access?.read ? " mode-read" : "") +
        (episode.id === currentEpisodeId ? " mode-current" : "")
      }
    >
      <Link
        href={`/episodes/${episode.id}`}
        className="series-eplist-item-link"
        {...makeClickLogDataAttrs({
          logCategory: LOG_CATEGORIES.list,
          logEventDetail: "clk-thumb",
          logPosition: String(displayIndex),
          logArticleId: episode.indexId,
          logImageId: imgId,
        })}
        onClick={handleOnClick}
      >
        <figure className="series-eplist-item-fig">
          <ImageWithImpLog
            src={images[0].url}
            width={images[0].width || 1200}
            height={images[0].height || 630}
            alt={`${authorName}さんの作品: ${episode.title}`}
            className="series-eplist-item-img"
            {...makeImpLogDataAttrs({
              logEventDetail: displayedOn + "_top",
              logCategory: "list",
              logPosition: String(displayIndex),
              logImageId: imgId,
              logArticleId: episode.indexId,
            })}
          />
        </figure>
        <div className="series-eplist-item-main">
          <h2 className="series-eplist-item-h">
            <span className="series-eplist-item-h-text">{episode.title}</span>
          </h2>
          <div className="series-eplist-item-meta">
            <div className="series-eplist-item-meta-date">{publishDate}</div>
            <div className="series-eplist-item-meta-fav">
              <MdFavoriteBorder size={14} className="series-eplist-item-meta-icon" />
              <span className="series-eplist-item-meta-count">{episode.numLikes}</span>
            </div>
          </div>
        </div>
        <div className="series-eplist-item-access">
          {access && (
            <EpisodeIcon
              access={access}
              waitAndFreeWaiting={waitAndFreeWaiting}
              showWaitAndFreeIcon={true}
            />
          )}
        </div>
      </Link>
      <AdminEpisodeOptions mode="list" episodeId={episode.id} />
    </div>
  );
}
